/* © Facilogi. See COPYRIGHT file for full copyright & licensing details. */

import Configuration from "./Configuration";
import Coupons from "./Coupons";
import Customers from "./Customers";
import Products from "./Products";
import Plans from "./Plans";
import Prices from "./Prices";
import PriceTargets from "./PriceTargets";
import Subscriptions from "./Subscriptions";
import Payments from "./Payments";
import AdsPublication from "./AdsPublication";
import Roles from "./Roles";
import ConsoleUsers from "./ConsoleUsers";
import Activate from "./Activate";
import ForgotPassword from "./ForgotPassword";
import ResetPassword from "./ResetPassword";
import Home from "./Home";
import AgentKb from "./AgentKb";
import ApiKeys from "./ApiKeys";
import Newsletters from "./Newsletters";
import DesignCategories from "./DesignCategories";

export default {
    GLOBAL_appBrand: process.env.APP_BRAND || "",

    GLOBAL_defaultDateFormat: "MMM D, YYYY",
    GLOBAL_defaultDateTimeFormat: "MMM D, YYYY at HH:mm",
    GLOBAL_decimalSeparator: ".",
    GLOBAL_decimalThousandSeparator: ",",
    GLOBAL_unlimited: "unlimited {}",
    GLOBAL_default_standard_date_format: "MM/DD/YYYY",
    GLOBAL_defaultStandardDateTimeFormat: "DD/MM/YY HH:mm",

    GLOBAL_sessionExpired: "Your session has expired, please sign in again.",
    GLOBAL_invalidSession: "Invalid session, please sign in again.",

    SIGNIN_login: "Log in",
    SIGNIN_password: "Password",
    SIGNIN_logIn: "Log in",
    SIGNIN_forgotPassword: "Forgot your password ?",
    SIGNIN_showPassword: "Show password",
    SIGNIN_hidePassword: "Hide password",
    SIGNIN_email: "Email",
    SIGNIN_invalidCredentials: "Incorrect email or password.",
    SIGNIN_continue: "Continue",
    SIGNIN_back: "Back",

    SIGNIN_2faSetupTitle: "Set up two-factor authentication",
    SIGNIN_2faSetupIntro: "To secure your access, set up an authenticator app "
        + "(Google Authenticator, Authy, 1Password…).",
    SIGNIN_2faStepApp: "Install an authenticator app on your phone.",
    SIGNIN_2faStepKey: "Add an account and enter the key below.",
    SIGNIN_2faManualKey: "Setup key",
    SIGNIN_2faCodeLabel: "6-digit code",
    SIGNIN_2faCodePlaceholder: "123456",
    SIGNIN_2faActivate: "Activate",
    SIGNIN_2faVerifyTitle: "Two-step verification",
    SIGNIN_2faVerifyIntro: "Enter the code shown in your authenticator app.",
    SIGNIN_2faRecoveryHint: "You can also use a recovery code.",
    SIGNIN_totpEnterFreshCode: "The code expired. Enter the current code from your app.",

    SIGNIN_recoveryTitle: "Recovery codes",
    SIGNIN_recoveryIntro: "Keep these codes somewhere safe. Each works once and lets you sign in "
        + "if you lose your phone.",
    SIGNIN_recoveryCopy: "Copy codes",
    SIGNIN_recoveryAck: "I have saved my recovery codes.",
    SIGNIN_recoveryContinue: "Continue",

    USERS_accountDisabled: "This account is disabled.",
    USERS_totpEnrollmentRequired: "Two-factor setup required.",
    USERS_totpCodeRequired: "Enter your verification code.",
    USERS_totpCodeInvalid: "Incorrect verification code.",
    USERS_totpAlreadyEnrolled: "Two-factor authentication is already set up.",
    USERS_passwordTooShort: "The password is too short.",
    USERS_currentPasswordInvalid: "The current password is incorrect.",
    USERS_invalidResetToken: "This reset link is invalid or has expired.",
    USERS_emailRequired: "The email address is required.",
    GLOBAL_invalidUsernamePasswordCombination: "Incorrect email or password.",
    GLOBAL_save: "Save",

    PROFILE_title: "Your profile",
    PROFILE_groupGeneral: "General",
    PROFILE_navProfile: "Profile",
    PROFILE_navPreferences: "Preferences",
    PROFILE_navAuthentication: "Authentication",
    PROFILE_saved: "Your changes have been saved.",
    PROFILE_passwordSection: "Password",
    PROFILE_changePassword: "Change password",
    PROFILE_currentPassword: "Current password",
    PROFILE_newPassword: "New password",
    PROFILE_confirmPassword: "Confirm password",
    PROFILE_showPassword: "Show passwords",
    PROFILE_savePassword: "Update password",
    PROFILE_passwordChanged: "Your password has been changed.",
    PROFILE_passwordMismatch: "The passwords do not match.",

    PREFERENCES_timezone: "Time zone",
    PREFERENCES_timeFormat: "Time format",
    PREFERENCES_timeFormat12: "12-hour (e.g. 2:13 pm)",
    PREFERENCES_timeFormat24: "24-hour (e.g. 14:13)",

    AUTH_intro: "Strengthen your account security by requiring an extra step at login.",
    AUTH_2faTitle: "Two-factor authentication",
    AUTH_2faSub: "Use a code generated by an authenticator app.",
    AUTH_2faOn: "Enabled",
    AUTH_2faOff: "Disabled",
    AUTH_2faEnable: "Enable two-factor authentication",
    AUTH_2faDisable: "Disable",
    AUTH_2faDisableConfirm: "Disable two-factor authentication for your account?",
    AUTH_2faEnabled: "Two-factor authentication enabled.",
    AUTH_2faDisabled: "Two-factor authentication disabled.",
    AUTH_2faStepApp: "1. Install an authenticator app on your phone "
        + "(Google Authenticator, Authy...).",
    AUTH_2faStepKey: "2. Add an account and enter the key below.",
    AUTH_2faCodeLabel: "6-digit code",
    AUTH_2faCodePlaceholder: "123456",
    AUTH_2faActivate: "Activate",
    AUTH_recoveryIntro: "Keep these recovery codes somewhere safe. Each one works only once and "
        + "lets you sign in if you lose your phone.",
    AUTH_recoveryCopy: "Copy codes",
    AUTH_recoveryCopied: "Recovery codes copied.",
    AUTH_recoveryDone: "I saved my codes",
    AUTH_recoveryRegenerate: "Regenerate recovery codes",
    AUTH_sessionsTitle: "Active sessions",
    AUTH_sessionsSub: "These devices are currently signed into your account. "
        + "Sign out of any that you don't recognize.",
    AUTH_sessionsCurrent: "Current device",
    AUTH_sessionsSignOut: "Sign out",
    AUTH_sessionsSignOutConfirm: "Sign this device out? Its session will be revoked.",
    AUTH_sessionsSignedOut: "Device signed out",

    GLOBAL_attention: "Attention",
    GLOBAL_configuration: "Configuration",
    GLOBAL_products: "Products",
    GLOBAL_PRICE_TARGETS: "Price type",
    MAIN_MENU_dashboard: "Dashboard",
    MAIN_MENU_products: "Products",
    MAIN_MENU_plans: "Plans",
    MAIN_MENU_coupons: "Coupons",
    MAIN_MENU_yearlyCoupon: "Yearly coupon",
    MAIN_MENU_priceTargets: "Price types",
    MAIN_MENU_customers: "Customers",
    MAIN_MENU_users: "Users",
    MAIN_MENU_consoleTeam: "Console users",
    MAIN_MENU_consoleRoles: "Privilege roles",
    MAIN_MENU_configuration: "Configuration",
    MAIN_MENU_settings: "Settings",
    MAIN_MENU_subscriptionPlans: "Subscription plans",
    MAIN_MENU_subscriptions: "Subscriptions",
    MAIN_MENU_invoicing: "Payments",
    MAIN_MENU_agentKb: "Customer support",
    MAIN_MENU_api: "API",
    MAIN_MENU_newsletters: "Automatic newsletter",
    MAIN_MENU_apiKeys: "API keys",
    MAIN_MENU_designCategories: "Template categories",
    MAIN_MENU_agentKbGeneral: "Overview",
    MAIN_MENU_agentKbUsers: "By user",
    MAIN_MENU_agentKbConversations: "Conversations",
    MAIN_MENU_agentKbUsage: "Usage",
    MAIN_MENU_adsPublication: "Portails d’annonces",
    MAIN_MENU_adsPublicationGeneral: "General configuration",
    MAIN_MENU_adsPublicationSalePoint: "Customers and portals",
    MAIN_MENU_myAccount: "My account",
    MAIN_MENU_signOut: "Sign out",
    MAIN_MENU_whatsNew: "What's new?",
    MAIN_MENU_yourProfile: "Your profile",
    MAIN_MENU_resources: "Resources & guides",

    GLOBAL_help: "Help",
    GLOBAL_collapse: "Collapse menu",
    GLOBAL_expand: "Expand menu",
    GLOBAL_darkMode: "Dark mode",
    GLOBAL_lightMode: "Light mode",
    GLOBAL_actionDelete: "Delete",
    GLOBAL_actionUpdate: "Update",
    GLOBAL_actionDone: "I am done",

    GLOBAL_Language: "Language",
    GLOBAL_authenticationProblem: "User is not authenticated",

    GLOBAL_warning: "Warning",
    GLOBAL_fillRequiredField: "This field is required",
    GLOBAL_cancel: "Cancel",
    GLOBAL_internalError: "Internal server error",

    GLOBAL_tableActionSelected: "selected",
    GLOBAL_tableActionMultiSelected: "selected",

    GLOBAL_currency: "€",
    GLOBAL_excludingTaxSuffix: "excl. tax",
    GLOBAL_includingTaxSuffix: "incl. tax",
    GLOBAL_percentage: "%",
    GLOBAL_total: "Total",
    GLOBAL_unknown: "Unknown",
    GLOBAL_formSave: "Save",
    GLOBAL_next: "Next",
    GLOBAL_actions: "Actions",

    GLOBAL_confirm: "Confirm",
    GLOBAL_yes: "Yes",
    GLOBAL_no: "No",
    GLOBAL_Product: "Product",
    GLOBAL_CUSTOMER: "Customer",
    GLOBAL_COUPON: "Coupon",
    GLOBAL_Price: "Price",

    GLOBAL_actionArchive: "Archive",
    GLOBAL_statusActive: "Active",
    GLOBAL_statusArchived: "Archived",

    GLOBAL_filterName: "Name",
    GLOBAL_filterNamePlaceholder: "Filter by name",
    GLOBAL_filterStatus: "Status",
    GLOBAL_filterStatusPlaceholder: "Status",

    GLOBAL_value: "Value",
    GLOBAL_confirmAction: "Yes, I confirm",

    FILTER_or: " <b>or</b> ",
    FILTER_and: "<b>and</b>",
    FILTER_add: "Add a filter",
    FILTER_apply: "Apply",
    FILTER_contains: "Contains",
    FILTER_value: "Value",
    FILTER_filterBy: "Filter by {0}",
    FILTER_and_x_more: " and {0} more",
    FILTER_propertyTagsId: "tag",
    FILTER_propertyTypeId: "type",
    FILTER_propertySubTypeId: "sub-type",

    GLOBAL_noResults: "No results",
    GLOBAL_search: "Search",

    GLOBAL_fieldSequence: "Sequence",

    GLOBAL_configurationCoupons: "Coupons",

    CONFIGURATION_navigationGeneral: "General",
    CONFIGURATION__yearlyCoupon: "Yearly coupon",

    TRANSLATIONS_en: "English",
    TRANSLATIONS_fr: "French",
    TRANSLATIONS_es: "Spanish",
    TRANSLATIONS_translated: "Translated",
    TRANSLATIONS_notTranslated: "Not translated",
    TRANSLATIONS_title: "Value translation",
    TRANSLATIONS_notificationTitle: "Translation",
    TRANSLATIONS_updateTitle: "Edit a translation",
    TRANSLATIONS_updated: "Translation updated successfully",

    ...Configuration,
    ...Coupons,
    ...Customers,
    ...Products,
    ...Plans,
    ...Prices,
    ...PriceTargets,
    ...AdsPublication,
    ...Subscriptions,
    ...Payments,
    ...Roles,
    ...ConsoleUsers,
    ...Activate,
    ...ForgotPassword,
    ...ResetPassword,
    ...Home,
    ...AgentKb,
    ...ApiKeys,
    ...Newsletters,
    ...DesignCategories
};
